import { useState, useEffect } from 'react';
import { Building, Search, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';

interface ChantierOption {
  id: string;
  nom_chantier: string;
  client: string | null;
}

interface ChantierSelectorProps {
  entrepriseId: string | null;
  excludeChantierId?: string;
  onSelect: (chantierId: string, chantierNom: string) => void;
  onCancel: () => void;
}

/**
 * Combobox inline pour sélectionner un chantier existant (bouton 🔗)
 * Affiché sous la ligne dans MultiOCRResults
 */
export default function ChantierSelector({
  entrepriseId,
  excludeChantierId,
  onSelect,
  onCancel,
}: ChantierSelectorProps) {
  const [chantiers, setChantiers] = useState<ChantierOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entrepriseId) return;

    const fetchChantiers = async () => {
      const { data, error } = await supabase
        .from('chantiers')
        .select('id, nom_chantier, client')
        .eq('entreprise_id', entrepriseId)
        .in('etat_chantier', ['actif', 'en_cours', 'planifie', 'prospect', 'devis'])
        .order('nom_chantier');

      if (!error && data) {
        const filtered = excludeChantierId
          ? data.filter(c => c.id !== excludeChantierId)
          : data;
        setChantiers(filtered);
      }
      setLoading(false);
    };

    fetchChantiers();
  }, [entrepriseId, excludeChantierId]);

  return (
    <div className="border rounded-md bg-background shadow-sm mt-1 mb-2">
      <div className="flex items-center justify-between px-2 pt-1">
        <span className="text-xs text-muted-foreground">Choisir un chantier</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onCancel}>
          <X className="h-3 w-3" />
        </Button>
      </div>
      <Command className="border-0">
        <CommandInput placeholder="Rechercher un chantier..." className="h-8 text-sm" />
        <CommandList className="max-h-[200px]">
          <CommandEmpty>
            {loading ? 'Chargement...' : 'Aucun chantier trouvé'}
          </CommandEmpty>
          <CommandGroup>
            {chantiers.map((c) => (
              <CommandItem
                key={c.id}
                value={`${c.nom_chantier} ${c.client || ''}`}
                onSelect={() => onSelect(c.id, c.nom_chantier)}
                className="text-sm cursor-pointer"
              >
                <Building className="h-3.5 w-3.5 mr-2 text-muted-foreground flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="truncate font-medium">{c.nom_chantier}</span>
                  {c.client && (
                    <span className="text-xs text-muted-foreground truncate">{c.client}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}
